import type { TableProps } from 'antd';
import { Alert, Button, Form, Modal } from 'antd';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom-v5-compat';
import { SIMULATION_ID_SEARCH_PARAM } from '../../../pages/Simulation/SimulationLandingPage';
import { useStoreActions, useStoreState } from '../../../store';
import TntManagementContainer from '../../timeInTransit/TntManagementContainer';
import { TntUpload } from '../../timeInTransit/TntUpload';
import { TimeInTransitOptionsForm } from '../PartnerConfigurationOverrideForms/TimeInTransitOptionsForm';
import { BASE_DETAILS_WORKFLOW_KEY } from './SimulationBaseDetailsStep';
import { SimulationWorkflowCard } from './SimulationWorkflowCard';
import { useUpdateSimulation } from '~/hooks/simulation/useSimulationSetupMutations';
import { PlusOutlined } from '@ant-design/icons';
import { flattenTntOptionsToFormData, shapeTntOptions } from '../utils';
import { TNT_OVERRIDE_MESSAGE } from '../../../store/timeInTransit/types';
import type { TimeInTransitModelSource, TimeInTransitSetting } from '../../../store/simulation/types';

export const TNT_OVERRIDE_STEP_KEY = 'simulationTntOptions';

export const TNT_OVERRIDE_DEFAULT_OPTIONS = {
  timeInTransitSetting: 'conservative' as TimeInTransitSetting,
  timeInTransitModelSource: 'shipment_delivery_prediction' as TimeInTransitModelSource,
};

export const TntOverrideStep = () => {
  const setupData = useStoreState((state) => state.simulation.simulationSetupData);

  const { mutateAsync: updateSimulation } = useUpdateSimulation();
  const activeNavigationStep = useStoreState((state) => state.simulation.activeNavigationStep);
  const setActiveNavigationStep = useStoreActions((actions) => actions.simulation.setActiveNavigationStep);
  const setSimulationSetupData = useStoreActions((actions) => actions.simulation.setSimulationSetupData);
  const activePartner = useStoreState((state) => state.partner.activePartner);

  const [query] = useSearchParams();
  const simulationId = query.get(SIMULATION_ID_SEARCH_PARAM);
  const [form] = Form.useForm();

  const [uploadModalOpen, setUploadModalOpen] = useState(false);

  const simulationTntOptions = setupData[TNT_OVERRIDE_STEP_KEY] || {
    timeInTransitSourcePreference:
      activePartner?.deliveryExperienceConfigurationInfo?.timeInTransitSourcePreference || [],
    weekendConsideredTransitDay:
      activePartner?.deliveryExperienceConfigurationInfo?.weekendConsideredTransitDay || false,
    ...TNT_OVERRIDE_DEFAULT_OPTIONS,
  };

  const { tntOverride } = simulationTntOptions;
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>(tntOverride ? [tntOverride] : []);
  const selectedTnt = selectedRowKeys[0] || '';

  useEffect(() => {
    if (simulationId && tntOverride) {
      setSelectedRowKeys([tntOverride]);
    }
  }, [simulationId, tntOverride]);

  const handleSave = async () => {
    try {
      if (simulationId) {
        const formValues = await form.validateFields();

        // ensure partner_provided is included in the source preference if TNT override is selected
        if (selectedTnt) {
          const tntSourcePreference = formValues.timeInTransitSourcePreference?.length
            ? formValues.timeInTransitSourcePreference
            : activePartner?.deliveryExperienceConfigurationInfo?.timeInTransitSourcePreference || [];
          if (!tntSourcePreference.includes('partner_provided')) {
            tntSourcePreference.unshift('partner_provided');
            formValues.timeInTransitSourcePreference = tntSourcePreference;
          }
        }

        const simulationTntOptions = shapeTntOptions({
          ...formValues,
          tntOverride: selectedTnt,
          tntOverrideDataSetId: selectedTnt,
        }) as any;

        const modifiedSetupData = {
          ...setupData,
          [TNT_OVERRIDE_STEP_KEY]: simulationTntOptions,
        };

        await updateSimulation({
          simulationId,
          [TNT_OVERRIDE_STEP_KEY]: simulationTntOptions,
          simulationSetupData: modifiedSetupData,
        });
        setSimulationSetupData(modifiedSetupData);
        setActiveNavigationStep(activeNavigationStep + 1);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const rowSelection: TableProps<any>['rowSelection'] = {
    type: 'checkbox',
    selectedRowKeys,
    hideSelectAll: true,
    onChange: (selectedKeys) => {
      // select only one, use checkboxes to allow deselect
      const newSelectedKeys = selectedKeys.length ? [selectedKeys[selectedKeys.length - 1]] : [];
      setSelectedRowKeys(newSelectedKeys);
    },
  };

  return (
    <SimulationWorkflowCard simulationId={setupData[BASE_DETAILS_WORKFLOW_KEY]?.simulationId ?? ''} onSave={handleSave}>
      <Alert
        description="(Optional) For this simulation, override the production time in transit (TNT) values."
        style={{ marginBottom: '10px' }}
        type="info"
        showIcon
      />
      <Form form={form} initialValues={flattenTntOptionsToFormData(simulationTntOptions)}>
        <TimeInTransitOptionsForm />
        {selectedTnt ? (
          <Alert style={{ marginBottom: '10px' }} message={TNT_OVERRIDE_MESSAGE} />
        ) : (
          <div style={{ height: '50px' }} />
        )}
        <div
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}
          className="ant-col ant-form-item-label"
        >
          <h3>Time in Transit Override File</h3>
          <div
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '5px' }}
            className="ant-col ant-form-item-label"
          >
            <Button onClick={() => setUploadModalOpen(true)}>
              Upload TNT
              <PlusOutlined />
            </Button>
          </div>
        </div>
        <Modal
          width={'900px'}
          open={uploadModalOpen}
          destroyOnClose={true}
          onCancel={() => setUploadModalOpen(false)}
          onOk={() => setUploadModalOpen(false)}
        >
          <TntUpload type="simulation" />
        </Modal>
        <TntManagementContainer
          showTabs={false}
          showTitle={false}
          showUpload={false}
          removeColumns={['actions', 'version']}
          simulationTableProps={{
            rowSelection,
            pagination: {
              pageSize: 15,
            },
          }}
        />
      </Form>
    </SimulationWorkflowCard>
  );
};
